import { useMemo } from 'react';
import pluralize from 'pluralize';

// Components
import { U21Button, U21Chip, U21NoValue } from 'app/shared/u21-ui/components';
import { DeadlineTooltip } from 'app/modules/deadlines/components/DeadlineTooltip';

// Models
import { ArticleDeadline, DeadlinesConfig } from 'app/modules/deadlines/models';

// Utils
import { isPast, formatDistanceToNow } from 'date-fns';
import { getDeadlineChipStatus } from 'app/modules/deadlines/utils';

// Constants
import { CHIP_STATUS_TO_STYLE } from 'app/modules/deadlines/constants';
import { IconPlus } from '@u21/tabler-icons';

interface OwnProps {
  articleDeadline?: ArticleDeadline;
  deadlinesConfig?: DeadlinesConfig;
  closed?: boolean;
  onClick?: () => void;
}

export const DeadlineChip = ({
  articleDeadline,
  deadlinesConfig,
  closed,
  onClick,
}: OwnProps) => {
  const {
    deadline,
    show_warning: showWarning,
    duration,
    days_remaining: daysRemaining,
    days_remaining_unit: daysRemainingUnit,
  } = articleDeadline ?? {};
  const {
    title,
    calc_start_date: calcStartDate,
    duration: configDuration,
  } = deadlinesConfig ?? {};

  const ms: number | undefined = useMemo(
    () => (!closed && deadline && Date.parse(deadline)) || undefined,
    [deadline, closed],
  );

  const pastDue = useMemo(() => (ms && isPast(ms)) || undefined, [ms]);

  const text = useMemo(() => {
    if (!ms) {
      return '';
    }
    // Only business-day queues need an explicit day count — calendar/adjusted
    // modes already baked the due date, so fall back to wall-clock distance
    // (hours / days / months) like pre-bizday chips.
    if (daysRemainingUnit === 'business' && daysRemaining != null) {
      if (daysRemaining === 0) {
        return 'Due today';
      }
      const unit = pluralize('business day', Math.abs(daysRemaining), true);
      return daysRemaining < 0 ? `${unit} past due` : `${unit} remaining`;
    }
    const days = formatDistanceToNow(ms);
    return pastDue ? `${days} past due` : `${days} remaining`;
  }, [ms, pastDue, daysRemaining, daysRemainingUnit]);

  const chipProps = useMemo(() => {
    return CHIP_STATUS_TO_STYLE[
      getDeadlineChipStatus(ms, pastDue, showWarning)
    ];
  }, [ms, pastDue, showWarning]);

  if (!ms) {
    if (onClick) {
      return (
        <U21Button
          aria-label="Add deadline"
          icon={<IconPlus />}
          size="small"
          onClick={onClick}
        />
      );
    }
    return <U21NoValue />;
  }

  return (
    <DeadlineTooltip
      title={title}
      duration={duration}
      calcStartDate={calcStartDate}
      manualDueDate={configDuration !== duration}
      businessDays={daysRemainingUnit === 'business'}
    >
      <U21Chip {...chipProps} onClick={onClick}>
        {text}
      </U21Chip>
    </DeadlineTooltip>
  );
};
